/*
 * Copyright (c) 2022. Revo Digital
 * ---
 * Author: gabriele
 * File: ControlGroup.ts
 * Project: mle-client-n
 * Committed last: 2022/1/24 @ 1251
 * ---
 * Description:
 */

import { ControlTypes }       from '../../selection-properties';
import { ShapeTypes }         from '../../enums';
import {
  ObservableShape
}                             from '../../../store/observables/ObservableShape';
import { getShapeTypeFromId } from '../../utils';
import {
  ControlFieldTransformer
}                             from '../../../components/editor/controls/ShapeControlView';
import {
  ButtonGroupAction
}                             from '../../../components/editor/controls/ButtonGroupControlInput';

export enum ControlGroupLayouts {
  FLEX_ROW,
  WHOLE_ROW,
  POPOVER
}

export interface HasTitleAndDescription {
  title: string;
  description: string;
}

export interface ShapeControlInitParams<T> {
  label: string;
  controlType: ControlTypes;
  observableValue: T;
  setter: (value: T) => any;
  transformer?: ControlFieldTransformer<T>;
  displayMeasureUnit?: boolean;
  measureUnitOverride?: string;
  controlledShapesOverride?: ShapeTypes[];
  lockable?: boolean;
  tooltip?: string;
  isReadonly?: boolean;
}

export class ShapeControl<T> {
  private _controlType: ControlTypes;
  private _displayMeasureUnit: boolean;
  constructor(initParams: ShapeControlInitParams<T>) {
    this._controlType = initParams.controlType;
    this._displayMeasureUnit = !!initParams.displayMeasureUnit;
    this._label = initParams.label;
    this._lockable = !!initParams.lockable;
    this._tooltip = initParams.tooltip ?? '';
    this._setter = initParams.setter;
    this._observableValue = initParams.observableValue;
    this._transformer = initParams.transformer;
    this._measureUnitOverride = initParams.measureUnitOverride;
    this._controlledShapesOverride = initParams.controlledShapesOverride;
    this._isReadonly = initParams.isReadonly === undefined ? false : initParams.isReadonly;
  }

  private _label: string;
  private _tooltip: string;
  private _lockable: boolean;
  private _observableValue: T;
  private _setter: (value: T) => any;
  private _transformer?: ControlFieldTransformer<T>;
  private _measureUnitOverride?: string;
  private _controlledShapesOverride?: ShapeTypes[];

  private _isReadonly: boolean;

  public get isReadonly(): boolean {
    return this._isReadonly;
  }

  public set isReadonly(value: boolean) {
    this._isReadonly = value;
  }

  public get controlledShapesOverride(): ShapeTypes[] | undefined {
    return this._controlledShapesOverride;
  }

  public set controlledShapesOverride(value: ShapeTypes[] | undefined) {
    this._controlledShapesOverride = value;
  }

  public get measureUnitOverride(): string | undefined {
    return this._measureUnitOverride;
  }

  public set measureUnitOverride(value: string | undefined) {
    this._measureUnitOverride = value;
  }

  public get observableValue(): T {
    return this._observableValue;
  }

  public set observableValue(value: T) {
    this._observableValue = value;
  }

  public get setter(): (value: T) => any {
    return this._setter;
  }

  public set setter(value: (value: T) => any) {
    this._setter = value;
  }

  public get transformer(): ControlFieldTransformer<T> | undefined {
    return this._transformer;
  }

  public set transformer(value: ControlFieldTransformer<T> | undefined) {
    this._transformer = value;
  }

  public get tooltip(): string {
    return this._tooltip;
  }

  public set tooltip(value: string) {
    this._tooltip = value;
  }

  public get controlType(): ControlTypes {
    return this._controlType;
  }

  public set controlType(value: ControlTypes) {
    this._controlType = value;
  }

  public get displayMeasureUnit(): boolean {
    return this._displayMeasureUnit;
  }

  public set displayMeasureUnit(value: boolean) {
    this._displayMeasureUnit = value;
  }

  public get label(): string {
    return this._label;
  }

  public set label(value: string) {
    this._label = value;
  }

  public get lockable(): boolean {
    return this._lockable;
  }

  public set lockable(value: boolean) {
    this._lockable = value;
  }

  public overrideControls(shape: ObservableShape<any>) {
    if (!this.controlledShapesOverride) return false;
    return this.controlledShapesOverride.includes(getShapeTypeFromId(shape.pamelaShape__nonObservable.id()));
  }
}

export const controlGroupBuilder = (initParams: ControlGroupInitParams): ControlGroup => {
  return new ControlGroup(initParams);
};

export interface ControlGroupInitParams {
  title: string;
  layout: ControlGroupLayouts;
  shapeControls: ShapeControl<any>[];
  controlledShapeTypes: ShapeTypes[];
  divide?: boolean;
  infoCallout?: HasTitleAndDescription;
}

export class ButtonGroupShapeControl<T> extends ShapeControl<T> {
  private _options: ButtonGroupAction<T>[];

  constructor(initParams: ShapeControlInitParams<T>, options: ButtonGroupAction<T>[]) {
    super(initParams);
    this._options = options;
  }

  public get options(): ButtonGroupAction<T>[] {
    return this._options;
  }

  public set options(value: ButtonGroupAction<T>[]) {
    this._options = value;
  }
}

export class ControlGroup {
  private _title: string;
  private _layout: ControlGroupLayouts;
  private _shapeControls: ShapeControl<any>[];
  private readonly controlledShapeTypes: ShapeTypes[];
  private readonly _divide: boolean;
  private _infoCallout?: HasTitleAndDescription;

  constructor(initParams: ControlGroupInitParams) {
    this._title = initParams.title;
    this._layout = initParams.layout;
    this._shapeControls = initParams.shapeControls;
    this.controlledShapeTypes = initParams.controlledShapeTypes;
    this._divide = initParams.divide || false;
    this._infoCallout = initParams.infoCallout;
  }

  public get infoCallout(): HasTitleAndDescription | undefined {
    return this._infoCallout;
  }

  public set infoCallout(value: HasTitleAndDescription | undefined) {
    this._infoCallout = value;
  }

  public get title(): string {
    return this._title;
  }

  public set title(value: string) {
    this._title = value;
  }

  public get divide(): boolean {
    return this._divide;
  }

  public get layout(): ControlGroupLayouts {
    return this._layout;
  }

  public set layout(value: ControlGroupLayouts) {
    this._layout = value;
  }

  public get shapeControls(): ShapeControl<any>[] {
    return this._shapeControls;
  }

  public set shapeControls(value: ShapeControl<any>[]) {
    this._shapeControls = value;
  }

  public controls(shape: ObservableShape<any>, options: { includeOverrides: boolean } = { includeOverrides: false }): boolean {
    const hasOverrides = this._shapeControls.some((control) => control.controlledShapesOverride?.includes(
      getShapeTypeFromId(shape.pamelaShape__nonObservable.id())));

    return this.controlledShapeTypes.includes(getShapeTypeFromId(shape.pamelaShape__nonObservable.id())) || (options.includeOverrides && hasOverrides);
  }
}
